/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/_current/environment';
import {
  GroupChallenge,
  GroupChallengeFilter,
  GroupChallengeV2,
  GroupChallengeWithTeams,
  ProgramWorkoutSchedule,
} from '@skyfit/core-data-library';
import { map, Observable } from 'rxjs';
import { AdminTenantService } from '../admin-tenant/admin-tenant.service';
import { TranslatorService } from '../translator/translator.service';
export interface GroupChallengeResponse {
  groupChallenges?: GroupChallenge[];
}
@Injectable({
  providedIn: 'root',
})
export class AdminChallengesService {
  private readonly http = inject(HttpClient);
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly translationService = inject(TranslatorService);

  getGroupChallenges(): Observable<GroupChallenge[]> {
    const path = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges`;
    return this.http.get(path).pipe(
      map((res: GroupChallengeResponse) => {
        const result = res.groupChallenges;
        return result;
      })
    );
  }

  getGroupChallengesByTenant(
    includeDates?: boolean
  ): Observable<GroupChallenge[]> {
    const tenantId = this.adminTenantService.getTenantId();
    let path = `/api/content/v1/groupChallenges?tenant=${tenantId}`;
    if (includeDates) {
      path += '&startDates=true';
    }
    const url = `${environment.aaptivServiceUrl}${path}`;
    return this.http.get(url).pipe(
      map((res: GroupChallengeResponse) => {
        const result = res.groupChallenges;
        return result;
      })
    );
  }

  getGroupChallenge(id: string): Observable<GroupChallenge> {
    const path = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${id}`;

    return this.http.get(path).pipe(
      map((res: GroupChallenge) => {
        const result = res;
        return result;
      })
    );
  }

  getGroupChallengeV2(id: string): Observable<GroupChallengeV2> {
    const path = `${environment.aaptivServiceUrl}/api/v2/user/groupChallenge/${id}?includeScore=true`;
    return this.http.get<GroupChallengeV2>(path);
  }

  getGroupChallengeScores(id: string): Observable<GroupChallengeWithTeams> {
    const path = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${id}/scores`;
    return this.http.get<GroupChallengeWithTeams>(path);
  }

  searchChallenges(
    filter: GroupChallengeFilter
  ): Observable<{ groupChallenges: GroupChallenge[] }> {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges`;

    const params = new HttpParams({ fromObject: { ...filter } });
    return this.http.get<{ groupChallenges: GroupChallenge[] }>(url, {
      params,
    });
  }

  getChallenge(id: number): Observable<GroupChallenge> {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${id}`;
    return this.http.get<GroupChallenge>(url);
  }

  encodeChallengeId(id: number): string {
    return btoa((id * new Date().getFullYear()).toString(16));
  }

  decodeChallengeId(encodedId: string): number {
    return Number.parseInt(atob(encodedId), 16) / new Date().getFullYear();
  }

  canTenantAccessChallenge(
    challenge: { isShareable?: boolean; accessControlKey?: string },
    tenantId: string
  ): boolean {
    return challenge.isShareable || challenge.accessControlKey === tenantId;
  }

  getChallengeScheduleByCategoryId(
    categoryId: string,
    startDate?: Date
  ): Observable<ProgramWorkoutSchedule> {
    const url = `${environment.aaptivServiceUrl}/api/v1/category/${categoryId}/overview`;
    const startDateValue = (startDate ?? new Date())
      .toISOString()
      .substring(0, 10);

    const params = new HttpParams().append('startDate', startDateValue);

    return this.http.get<ProgramWorkoutSchedule>(url, { params });
  }

  createGroupChallenge(challenge: GroupChallenge) {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges`;

    const create: GroupChallenge = {
      ...challenge,
      createTime: undefined,
      updateTime: undefined,
      groupChallengeStarts: undefined,
      groupChallengeId: undefined,
    };

    return this.http.post<GroupChallenge>(url, create);
  }

  updateGroupChallenge(challenge: GroupChallenge) {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${challenge.groupChallengeId}`;

    const update: GroupChallenge = {
      ...challenge,
      createTime: undefined,
      updateTime: undefined,
      groupChallengeStarts: undefined,
      groupChallengeId: undefined,
    };

    return this.http.put<GroupChallenge>(url, update);
  }

  createGroupChallengeDates(
    challengeId: number,
    signupDate: string,
    startDate: string
  ) {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${challengeId}/start`;

    return this.http.post(url, {
      signupTime: signupDate,
      startTime: startDate,
    });
  }

  updateGroupChallengeDates(
    challengeId: number,
    startId: number,
    signupDate: string,
    startDate: string
  ) {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${challengeId}/start/${startId}`;
    return this.http.put(url, {
      signupTime: signupDate,
      startTime: startDate,
    });
  }

  deleteGroupChallenge(id: number) {
    const url = `${environment.aaptivServiceUrl}/api/content/v1/groupChallenges/${id}`;
    return this.http.delete(url);
  }

  formatDuration(value: number): string {
    if (!value) {
      return '';
    }
    const weeks = ~~(value / 7);
    return this.formatWeeks(weeks);
  }

  formatWeeks(value: number): string {
    const key = value > 1 ? 'challenges.weeks' : 'challenges.week';
    return this.translationService.translate(key, [value]);
  }
}
